import type { UseTranslationResponse } from 'react-i18next';
import { useTranslation } from 'react-i18next';

export const namespace = 'create-handler';
export const keyPrefix = 'create_handler';

export const useCreateHandlerTranslation = (): UseTranslationResponse<
  typeof namespace,
  typeof keyPrefix
> => useTranslation(namespace, { keyPrefix });

export const ruRu = {
  [namespace]: {
    [keyPrefix]: {
      breadcrumb: {
        handlers: 'Сценарии',
        new: 'Новый сценарий',
      },
      title: 'Выберите тип сценария, который хотите создать',
      other_scenario: {
        creation_title: 'Создание сценария',
        basic_parameters: 'Базовые параметры',
        name: 'Название',
      },
      cards: {
        biometric: {
          title: 'Биометрическая идентификация лиц без сохранения в список',
          description: 'Сценарий позволяет обнаружить все лица на кадре и сравнить их со всеми лицами в указанном списке.',
        },
        attributes: {
          title: 'Определение атрибутов и свойств лица (пол, возраст, эмоции и др.) без идентификации',
          description: 'Сценарий позволяет обнаружить все лица на кадре, выполнить все возможные проверки и сохранить событие.',
        },
        other: {
          title: 'Другой',
          description: 'Сценарий без преднастроек. Сможете настроить параметры вручную после создания.',
        },
      },
      form: {
        biometric_description: 'Сценарий «Биометрическая идентификация лиц без сохранения в список» позволяет обнаружить все лица на кадре и сравнить их со всеми лицами в указанном списке.',
        attributes_description: 'Сценарий «Определение атрибутов и свойств лица (пол, возраст, эмоции и др.) без идентификации» позволяет обнаружить все лица на кадре, выполнить все возможные проверки и сохранить событие.',
        name: 'Наименование сценария',
        owner_department: 'Подразделение владелец',
        list: 'Список для поиска',
        create: 'Создать',
      },
      success: {
        title: 'Сценарий {{- description}} успешно создан и сохранен',
        text: 'Уникальный идентификатор сценария:<br><strong>{{handlerId}}</strong>.',
        description: 'Проверить и изменить настройки сценария можно <a>здесь</a>',
      },
    },
  },
};

export const en = {
  [namespace]: {
    [keyPrefix]: {
      breadcrumb: {
        handlers: 'Policies',
        new: 'New policy',
      },
      title: 'Select the type of policy you want to create',
      other_scenario: {
        creation_title: 'Create policy',
        basic_parameters: 'Basic parameters',
        name: 'Name',
      },
      cards: {
        biometric: {
          title: 'Biometric face identification without saving to a list',
          description: 'Detect all faces in a frame and compare them with every face in the selected list.',
        },
        attributes: {
          title: 'Face attribute detection without identification',
          description: 'Detect all faces in a frame, perform all available checks, and save the event.',
        },
        other: {
          title: 'Other',
          description: 'A policy without presets. You can configure its parameters manually after creation.',
        },
      },
      form: {
        biometric_description: 'The “Biometric face identification without saving to a list” policy detects all faces in a frame and compares them with every face in the selected list.',
        attributes_description: 'The “Face attribute detection without identification” policy detects all faces in a frame, performs all available checks, and saves the event.',
        name: 'Policy name',
        owner_department: 'Owner department',
        list: 'List to search',
        create: 'Create',
      },
      success: {
        title: 'The policy {{- description}} has been successfully created and saved.',
        text: 'Policy unique identifier:<br><strong>{{handlerId}}</strong>.',
        description: 'You can check and edit the policy settings <a>here</a>',
      },
    },
  },
};

export const esEs = {
  [namespace]: {
    [keyPrefix]: {
      breadcrumb: {
        handlers: 'Escenarios',
        new: 'Nuevo escenario',
      },
      title: 'Seleccione el tipo de escenario que desea crear',
      other_scenario: {
        creation_title: 'Crear escenario',
        basic_parameters: 'Parámetros básicos',
        name: 'Nombre',
      },
      cards: {
        biometric: {
          title: 'Identificación biométrica facial sin guardar en una lista',
          description: 'Detecta todos los rostros de un fotograma y los compara con los rostros de la lista seleccionada.',
        },
        attributes: {
          title: 'Detección de atributos faciales sin identificación',
          description: 'Detecta todos los rostros, realiza las comprobaciones disponibles y guarda el evento.',
        },
        other: {
          title: 'Otro',
          description: 'Un escenario sin ajustes predefinidos. Puede configurar sus parámetros después de crearlo.',
        },
      },
      form: {
        biometric_description: 'El escenario de identificación biométrica facial detecta todos los rostros de un fotograma y los compara con los rostros de la lista seleccionada.',
        attributes_description: 'El escenario de detección de atributos faciales detecta todos los rostros, realiza las comprobaciones disponibles y guarda el evento.',
        name: 'Nombre del escenario',
        owner_department: 'Departamento propietario',
        list: 'Lista de búsqueda',
        create: 'Crear',
      },
      success: {
        title: 'El escenario {{- description}} se ha creado y guardado correctamente.',
        text: 'Identificador único del escenario:<br><strong>{{handlerId}}</strong>.',
        description: 'Puede consultar y cambiar la configuración del escenario <a>aquí</a>',
      },
    },
  },
};

export const ptBr = {
  [namespace]: {
    [keyPrefix]: {
      breadcrumb: {
        handlers: 'Cenários',
        new: 'Novo cenário',
      },
      title: 'Selecione o tipo de cenário que deseja criar',
      other_scenario: {
        creation_title: 'Criar cenário',
        basic_parameters: 'Parâmetros básicos',
        name: 'Nome',
      },
      cards: {
        biometric: {
          title: 'Identificação biométrica facial sem salvar em uma lista',
          description: 'Detecta todos os rostos em um quadro e os compara com os rostos da lista selecionada.',
        },
        attributes: {
          title: 'Detecção de atributos faciais sem identificação',
          description: 'Detecta todos os rostos, executa as verificações disponíveis e salva o evento.',
        },
        other: {
          title: 'Outro',
          description: 'Um cenário sem predefinições. Você pode configurar os parâmetros após a criação.',
        },
      },
      form: {
        biometric_description: 'O cenário de identificação biométrica facial detecta todos os rostos em um quadro e os compara com os rostos da lista selecionada.',
        attributes_description: 'O cenário de detecção de atributos faciais detecta todos os rostos, executa as verificações disponíveis e salva o evento.',
        name: 'Nome do cenário',
        owner_department: 'Departamento proprietário',
        list: 'Lista para pesquisa',
        create: 'Criar',
      },
      success: {
        title: 'O cenário {{- description}} foi criado e salvo com sucesso.',
        text: 'Identificador exclusivo do cenário:<br><strong>{{handlerId}}</strong>.',
        description: 'Você pode verificar e alterar as configurações do cenário <a>aqui</a>',
      },
    },
  },
};
